/**
 * Multi-Agent SmartBot Component
 * 
 * This component uses the new multi-agent backend with specialized agents for:
 * - Job information CRUD (change pay, update hours, end employment)
 * - General knowledge Q&A (RAG)
 * - Future: Payroll operations, Pension management
 */

import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeRaw from "rehype-raw";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Bot, Send, Sparkles, X, CheckCircle2, AlertCircle } from "lucide-react";
import ApiService, { ApiError } from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { useChatWidgetStore } from "@/stores/useChatWidgetStore";

interface Message {
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  currentAgent?: string;
  needsConfirmation?: boolean;
}

interface MultiAgentSmartBotProps {
  currentPage?: string;
  employeeId?: number;
  employeeName?: string;
  onClose?: () => void;
  initialContext?: Record<string, any>;
  suggestedQuestions?: string[];
}

export const MultiAgentSmartBot: React.FC<MultiAgentSmartBotProps> = ({
  currentPage,
  employeeId,
  employeeName,
  onClose,
  initialContext,
  suggestedQuestions,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(`session_${Date.now()}`);
  const [conversationState, setConversationState] = useState<any>(null);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { initialQuestion, clearInitialQuestion } = useChatWidgetStore();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Start conversation with a greeting or initial question
    startConversation();
  }, []);

  const startConversation = async () => {
    if (initialQuestion) {
      sendMessage(initialQuestion);
      clearInitialQuestion();
    } else {
      const greetingMessage: Message = {
        role: "assistant",
        content:
          employeeId && employeeName
            ? `Hello! I'm your Hirelo AI assistant. I can help you manage ${employeeName}'s job information, answer questions about UK payroll, and more. What can I help you with?`
            : "Hello! I'm your Hirelo AI assistant. I can help you with employee management, UK payroll, tax compliance, and more. What can I help you with today?",
        timestamp: new Date(),
        currentAgent: "supervisor",
      };
      setMessages([greetingMessage]);
    }
  };

  const sendMessage = async (messageText?: string) => {
    const textToSend = messageText || input;
    if (!textToSend.trim() || loading) return;

    const userMessage: Message = {
      role: "user",
      content: textToSend,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const requestBody: any = {
        message: textToSend,
        session_id: sessionId,
      };

      // Add employee context if available
      if (employeeId) {
        requestBody.employee_id = employeeId;
      }
      if (employeeName) {
        requestBody.employee_name = employeeName;
      }

      // Add conversation state if exists (for multi-turn conversations)
      if (conversationState) {
        requestBody.conversation_state = conversationState;
      }
      if (initialContext) {
        requestBody.context = initialContext;
      }

      const response = await ApiService.request<{
        message: string;
        messages?: Array<{
          role: string;
          content: string;
          agent?: string;
        }>;
        session_id: string;
        conversation_state: any;
        current_agent: string;
        needs_confirmation: boolean;
        tool_results?: any;
      }>(EndpointKeys.CUSTOMER_AI_MULTI_AGENT_CHAT, {
        method: "POST",
        body: requestBody,
      });

      // Handle multiple messages from the backend (for multi-part responses)
      if (response.messages && response.messages.length > 0) {
        const newMessages: Message[] = response.messages.map((msg) => ({
          role: "assistant",
          content: msg.content,
          timestamp: new Date(),
          currentAgent: msg.agent || response.current_agent,
          needsConfirmation: response.needs_confirmation,
        }));
        setMessages((prev) => [...prev, ...newMessages]);
      } else {
        // Fallback to single message for backwards compatibility
        const assistantMessage: Message = {
          role: "assistant",
          content: response.message,
          timestamp: new Date(),
          currentAgent: response.current_agent,
          needsConfirmation: response.needs_confirmation,
        };
        setMessages((prev) => [...prev, assistantMessage]);
      }
      
      setConversationState(response.conversation_state);
      setNeedsConfirmation(response.needs_confirmation);

    } catch (error) {
      console.error("Error sending message:", error);
      
      let errorMessage = "I'm having trouble processing that. Please try again.";
      
      if (error instanceof ApiError) {
        errorMessage = error.message || errorMessage;
      }

      const errorMsg: Message = {
        role: "assistant",
        content: errorMessage,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInput(suggestion);
    sendMessage(suggestion);
  };

  return (
    <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 w-[480px] h-[700px] max-w-[95vw] max-h-[85vh] sm:max-w-[480px] sm:max-h-[700px] z-50 shadow-2xl rounded-lg overflow-hidden">
      <Card className="h-full flex flex-col">
        {/* Header */}
        <div className="bg-primary text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            <div>
              <h3 className="font-semibold">Hirelo AI Assistant</h3>
              {employeeName && (
                <p className="text-xs opacity-90">Managing: {employeeName}</p>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-white hover:bg-white/20"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Messages */}
        <CardContent className="flex-1 p-4 overflow-hidden">
          <ScrollArea className="h-full pr-4">
            <div className="space-y-4 min-w-0">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex min-w-0 ${
                    message.role === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[85%] rounded-lg p-3 break-words ${
                      message.role === "user"
                        ? "bg-primary text-white"
                        : "bg-gray-100 text-gray-900"
                    }`}
                  >
                    {/* Agent badge for assistant messages */}
                    {message.role === "assistant" && message.currentAgent && (
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Badge variant="secondary" className="text-xs shrink-0">
                          {message.currentAgent === "job_info" && "Job Info Agent"}
                          {message.currentAgent === "parent_info" && "Parent Info Agent"}
                          {message.currentAgent === "employer_info" && "Employer Info Agent"}
                          {message.currentAgent === "supervisor" && "Supervisor"}
                          {message.currentAgent === "general" && "General Knowledge"}
                        </Badge>
                        {message.needsConfirmation && (
                          <Badge variant="outline" className="text-xs shrink-0">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Confirmation Required
                          </Badge>
                        )}
                      </div>
                    )}
                    
                    {/* Message content with Markdown rendering for assistant; plain text for user */}
                    <div className="text-sm break-words overflow-wrap-anywhere prose prose-sm max-w-none">
                      {message.role === "assistant" ? (
                        <ReactMarkdown 
                          remarkPlugins={[remarkGfm, remarkBreaks]}
                          rehypePlugins={[rehypeRaw]}
                        >
                          {message.content}
                        </ReactMarkdown>
                      ) : (
                        <div className="whitespace-pre-wrap">{message.content}</div>
                      )}
                    </div>

                    <div className="text-xs opacity-70 mt-1">
                      {message.timestamp.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-lg p-3">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0.1s" }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0.2s" }}
                      ></div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>
        </CardContent>

        {/* Confirmation helper */}
        {needsConfirmation && (
          <div className="px-4 py-2 bg-yellow-50 border-t border-yellow-200">
            <p className="text-xs text-yellow-800 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Please confirm by saying "yes" or cancel with "no"
            </p>
          </div>
        )}

        {/* Quick actions for confirmations */}
        {needsConfirmation && (
          <div className="px-4 py-2 flex gap-2">
            <Button
              size="sm"
              variant="default"
              onClick={() => handleSuggestionClick("yes, proceed")}
              disabled={loading}
              className="flex-1"
            >
              <CheckCircle2 className="h-4 w-4 mr-1" />
              Confirm
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleSuggestionClick("no, cancel")}
              disabled={loading}
              className="flex-1"
            >
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
          </div>
        )}

        {/* Input */}
        <div className="p-4 border-t">
          {suggestedQuestions && suggestedQuestions.length > 0 && messages.length <= 1 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {suggestedQuestions.map((q, i) => (
                <Button
                  key={i}
                  variant="outline"
                  size="sm"
                  onClick={() => handleSuggestionClick(q)}
                >
                  {q}
                </Button>
              ))}
            </div>
          )}
          <div className="flex space-x-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                needsConfirmation
                  ? "Type 'yes' to confirm or 'no' to cancel..."
                  : "Type your message..."
              }
              disabled={loading}
              className="flex-1"
            />
            <Button onClick={() => sendMessage()} disabled={loading || !input.trim()}>
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default MultiAgentSmartBot;
